import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useApi } from "../api";
import { Card, PageHeader, Loading, ErrorNotice } from "./Ui";
import { label } from "../utils";
import { usePermissions } from "../permissions";
type Report = {
  generatedAt: string;
  scope: string;
  summary: Record<string, number>;
  sections: { title: string; columns: string[]; rows: (string | number)[][] }[];
};
export function ReportsPage() {
  const api = useApi();
  const permissions = usePermissions();
  const [kind, setKind] = useState("executive");
  const now = new Date();
  const [quarter, setQuarter] = useState(
    `Q${Math.floor(now.getMonth() / 3) + 1} ${now.getFullYear()}`,
  );
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const path = `/reports/management/${kind}?quarter=${encodeURIComponent(quarter)}`;
  const query = useQuery({
    queryKey: ["management-report", kind, quarter],
    queryFn: () => api.get<Report>(path),
  });
  return (
    <div className="page scroll-page executive-print">
      <PageHeader
        title="Management reports"
        description="Live portfolio insights, dependencies, review ageing and quarterly management context."
      />
      <div className="toolbar">
        <label>
          Report{" "}
          <select value={kind} onChange={(e) => setKind(e.target.value)}>
            {["executive", "portfolio", "dependency-risk", "intake-ageing"].map(
              (k) => (
                <option key={k} value={k}>
                  {label(k.replaceAll("-", " "))}
                </option>
              ),
            )}
          </select>
        </label>
        <label>
          Review quarter{" "}
          <select value={quarter} onChange={(e) => setQuarter(e.target.value)}>
            {Array.from(
              { length: 5 },
              (_, i) => now.getFullYear() - 2 + i,
            ).flatMap((y) =>
              [1, 2, 3, 4].map((q) => (
                <option key={`${y}-${q}`}>{`Q${q} ${y}`}</option>
              )),
            )}
          </select>
        </label>
        {permissions.businessCan("export", true) && <><button
          className="btn-primary"
          disabled={exporting || query.isPending || query.isError}
          onClick={() => {
            setExporting(true);
            setError(null);
            void api
              .download(path + "&format=csv", `strategic-${kind}.csv`)
              .catch((e) => setError(e))
              .finally(() => setExporting(false));
          }}
        >
          {exporting ? "Exporting…" : "Download report CSV"}
        </button>
        <button
          className="btn-outline"
          disabled={!query.data}
          onClick={() => window.print()}
        >
          Print / save PDF
        </button>
        </>}
      </div>
      {error && <ErrorNotice error={error} />}
      {query.isPending ? (
        <Loading />
      ) : query.isError ? (
        <ErrorNotice error={query.error} />
      ) : (
        <div className="content-grid">
          <Card className="span-2">
            <h2>Report summary</h2>
            <p>
              {query.data.scope} · Generated{" "}
              {new Date(query.data.generatedAt).toLocaleString()}
            </p>
            <div className="metric-list">
              {Object.entries(query.data.summary).map(([key, value]) => (
                <div className="metric-row" key={key}>
                  <span>{label(key.replace(/([A-Z])/g, " $1"))}</span>
                  <strong>{value}</strong>
                </div>
              ))}
            </div>
          </Card>
          {query.data.sections.map((section) => (
            <Card key={section.title} className="span-2">
              <h2>{section.title}</h2>
              {section.rows.length ? (
                <div className="table-wrap">
                  <table className="initiative-table">
                    <thead>
                      <tr>
                        {section.columns.map((c) => (
                          <th key={c}>{c}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {section.rows.map((r, i) => (
                        <tr key={i}>
                          {r.map((cell, j) => (
                            <td key={j}>{typeof cell === "string" && ["Workflow", "Decision", "Delivery", "Priority", "Board", "Freshness", "Capacity health"].includes(section.columns[j] ?? "") ? label(cell) : cell}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p>No matching records.</p>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
